<script lang="ts" setup>
import { onBeforeUnmount, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import VueMultiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import '../../views/Polizas/Polizas.scss';
import { useClientOptions } from '../../composables/useClientOptions';
import { IOperationClient } from '../../repositories/clientsABM.repository';
import { ICompany } from '../../types/company.type';
import { IAgents } from '../../types/agents.type';
import { OperationSearchData } from '../../repositories/operations.repository';

const props = defineProps<{
  typePolicyTransport: string;
  companies: ICompany[];
  agents: IAgents[];
  startDateLabel: string;
  endDateLabel: string;
  isDownloadingExcel?: boolean;
}>();

const emit = defineEmits<{
  search: [filters: OperationSearchData];
  reset: [];
  download: [];
}>();

const { t } = useI18n();
const {
  clientSelect,
  clientOptions,
  clientLoading,
  handleClientOpen,
  handleClientClose,
  handleClientSearch,
  disposeClientOptions,
} = useClientOptions(props.typePolicyTransport);

const filters = reactive({
  search: '',
  client: null as IOperationClient | null,
  company: null as ICompany | null,
  agent: null as IAgents | null,
  startDate: '',
  endDate: '',
});

onBeforeUnmount(() => {
  disposeClientOptions();
});

const handleSearch = () => {
  emit('search', {
    search: filters.search.trim() || null,
    clientId: filters.client?.id ?? null,
    companyId: filters.company?.id ?? null,
    agentId: filters.agent?.id ?? null,
    startDate: filters.startDate || null,
    endDate: filters.endDate || null,
  });
};

const resetSearch = () => {
  filters.search = '';
  filters.client = null;
  filters.company = null;
  filters.agent = null;
  filters.startDate = '';
  filters.endDate = '';
  emit('reset');
};
</script>

<template>
  <div class="poliza">
    <form class="poliza__form" @submit.prevent="handleSearch">
      <div class="poliza__fields">
        <div class="poliza__field">
          <label class="label-global" for="operation-search">
            {{ t('global.buscar') }}
          </label>
          <input
            id="operation-search"
            v-model="filters.search"
            class="input-global"
            type="text"
            :placeholder="t('operaciones.buscar_operacion')"
          />
        </div>

        <div class="poliza__field">
          <label class="label-global" for="operation-client">
            {{ t('global.cliente') }}
          </label>
          <VueMultiselect
            id="operation-client"
            ref="clientSelect"
            v-model="filters.client"
            class="poliza__multiselect"
            :options="clientOptions"
            :loading="clientLoading"
            :internal-search="false"
            :options-limit="Math.max(clientOptions.length, 20)"
            :show-labels="false"
            :allow-empty="true"
            track-by="id"
            label="tradeName"
            :placeholder="t('operaciones.filtro_cliente')"
            @search-change="handleClientSearch"
            @open="handleClientOpen"
            @close="handleClientClose"
          >
            <template #noResult>
              {{ t('polizas.polizas.sin_clientes') }}
            </template>
            <template #noOptions>
              {{ t('polizas.polizas.sin_clientes') }}
            </template>
          </VueMultiselect>
        </div>

        <div class="poliza__field">
          <label class="label-global" for="operation-company">
            {{ t('ajustes.configuracion_certificados.compania') }}
          </label>
          <VueMultiselect
            id="operation-company"
            v-model="filters.company"
            class="poliza__multiselect"
            :options="companies"
            :show-labels="false"
            :allow-empty="true"
            track-by="id"
            label="name"
            :placeholder="t('operaciones.filtro_compania')"
          />
        </div>

        <div class="poliza__field">
          <label class="label-global" for="operation-agent">
            {{ t('global.agente') }}
          </label>
          <VueMultiselect
            id="operation-agent"
            v-model="filters.agent"
            class="poliza__multiselect"
            :options="agents"
            :show-labels="false"
            :allow-empty="true"
            track-by="id"
            label="name"
            :placeholder="t('operaciones.filtro_agente')"
          />
        </div>

        <div class="poliza__field">
          <label class="label-global" for="operation-start-date">
            {{ startDateLabel }}
          </label>
          <VueDatePicker
            id="operation-start-date"
            v-model="filters.startDate"
            class="poliza__datepicker"
            :enable-time-picker="false"
            format="dd/MM/yyyy"
            model-type="yyyy-MM-dd"
            auto-apply
            :placeholder="startDateLabel"
          />
        </div>

        <div class="poliza__field">
          <label class="label-global" for="operation-end-date">
            {{ endDateLabel }}
          </label>
          <VueDatePicker
            id="operation-end-date"
            v-model="filters.endDate"
            class="poliza__datepicker"
            :enable-time-picker="false"
            format="dd/MM/yyyy"
            model-type="yyyy-MM-dd"
            auto-apply
            :placeholder="endDateLabel"
          />
        </div>
      </div>

      <div class="poliza__actions">
        <button
          class="background-blue button-global button__form"
          type="submit"
        >
          {{ t('global.buscar') }}
        </button>
        <button
          class="background-blue button-global button__form"
          type="button"
          @click="resetSearch"
        >
          {{ t('global.limpiar_filtro') }}
        </button>
        <button
          class="background-blue button-global button__form position-relative"
          type="button"
          :disabled="isDownloadingExcel"
          @click="emit('download')"
        >
          <span
            v-if="isDownloadingExcel"
            class="position-absolute top-50 start-50 translate-middle d-flex"
          >
            <BSpinner small />
          </span>
          <span :class="{ invisible: isDownloadingExcel }">{{ t('global.descargar_excel') }}</span>
        </button>
      </div>
    </form>
  </div>
</template>
